import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { LoadingSpinner } from '../ui/loading-states';
import { useDeleteLocation } from '../../hooks/locations/useLocations';
import type { Location } from '../../types/location.types';

interface DeleteLocationDialogProps {
  location: Location;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteLocationDialog: React.FC<DeleteLocationDialogProps> = ({
  location,
  isOpen,
  onClose,
}) => {
  const deleteMutation = useDeleteLocation();

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(location.id);
      onClose();
    } catch (error) {
      console.error('Failed to delete location:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Удаление локации
            </div>
          </DialogTitle>
          <DialogDescription>
            Вы уверены, что хотите удалить эту локацию? Это действие нельзя отменить.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium mb-2">{location.name}</h4>
            {location.address && (
              <p className="text-sm text-gray-600 mb-1">Адрес: {location.address}</p>
            )}
            <p className="text-sm text-gray-600">
              Радиус действия: {location.radius_meters} м
            </p>
            {location.requires_gps_validation && (
              <p className="text-sm text-green-600 mt-2">GPS проверка включена</p>
            )}
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              <strong>Внимание:</strong> При удалении локации будут также удалены:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-red-700">
              <li>• Все привязки сотрудников к этой локации</li>
              <li>• QR код этой локации</li>
              <li>• История посещений этой локации</li>
              <li>• Статистика по этой локации</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending}>
            Отмена
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending && <LoadingSpinner className="mr-2 h-4 w-4" />}
            Удалить локацию
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};